/**
 * Normalizes HTML previously saved by the Quill 2 editor (and by the legacy
 * PHP Summernote/CKEditor screens) into clean, semantic HTML that renders
 * correctly without any editor stylesheet:
 *
 * - Quill 2 stores every list as `<ol>` and marks bullets with
 *   `<li data-list="bullet">`; consumers reading the raw HTML see ordered
 *   lists. Items are regrouped into real `<ul>`/`<ol>` runs.
 * - `<span class="ql-ui">` marker spans (injected by Quill while editing) are
 *   removed.
 * - `ql-align-*`, `ql-indent-*`, `ql-direction-rtl`, `ql-font-*` and
 *   `ql-size-*` classes are converted to inline styles / `dir` attributes.
 * - `<iframe class="ql-video">` keeps its src and loses the Quill class.
 *
 * Plain HTML from other sources passes through untouched, so this is safe to
 * run on every value handed to the editor.
 */

const QL_FONTS: Record<string, string> = {
  serif: 'Georgia, Times New Roman, serif',
  monospace: 'Monaco, Courier New, monospace',
}

const QL_SIZES: Record<string, string> = {
  small: '0.75em',
  large: '1.5em',
  huge: '2.5em',
}

const hasQuillMarkup = (html: string) =>
  html.includes('data-list=') || html.includes('class="ql-') || html.includes("class='ql-")

export const isEmptyRichText = (html: string | null | undefined): boolean => {
  if (!html) return true
  const stripped = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()
  if (stripped) return false
  // Media-only content (images, videos, hr) is not empty.
  return !/<(img|iframe|video|hr|table)\b/i.test(html)
}

const regroupQuillLists = (root: HTMLElement) => {
  root.querySelectorAll('ol, ul').forEach((list) => {
    const items = Array.from(list.children).filter((el) => el.tagName === 'LI')
    if (!items.some((li) => li.hasAttribute('data-list'))) return

    const doc = root.ownerDocument
    const replacement: HTMLElement[] = []
    let currentTag: 'ul' | 'ol' | null = null
    let current: HTMLElement | null = null

    items.forEach((li) => {
      const kind = li.getAttribute('data-list')
      const tag: 'ul' | 'ol' = kind === 'bullet' ? 'ul' : 'ol'
      if (tag !== currentTag || !current) {
        current = doc.createElement(tag)
        replacement.push(current)
        currentTag = tag
      }
      li.removeAttribute('data-list')
      current.appendChild(li)
    })

    replacement.forEach((el) => list.parentNode?.insertBefore(el, list))
    list.remove()
  })
}

const convertQuillClasses = (root: HTMLElement) => {
  root.querySelectorAll('.ql-ui').forEach((el) => el.remove())

  root.querySelectorAll('[class*="ql-"]').forEach((el) => {
    const element = el as HTMLElement
    Array.from(element.classList).forEach((cls) => {
      let handled = true
      let match: RegExpMatchArray | null

      if ((match = cls.match(/^ql-align-(center|right|justify)$/))) {
        element.style.textAlign = match[1]
      } else if ((match = cls.match(/^ql-indent-(\d)$/))) {
        element.style.marginLeft = `${Number(match[1]) * 2}rem`
      } else if (cls === 'ql-direction-rtl') {
        element.setAttribute('dir', 'rtl')
        element.style.removeProperty('direction')
      } else if ((match = cls.match(/^ql-font-(.+)$/))) {
        element.style.fontFamily = QL_FONTS[match[1]] ?? match[1]
      } else if ((match = cls.match(/^ql-size-(.+)$/))) {
        if (QL_SIZES[match[1]]) element.style.fontSize = QL_SIZES[match[1]]
      } else if (cls === 'ql-video') {
        // handled below; just drop the class
      } else {
        handled = cls === 'ql-cursor' || cls === 'ql-clipboard'
      }

      if (handled) element.classList.remove(cls)
    })
    if (!element.getAttribute('class')) element.removeAttribute('class')
  })
}

export const normalizeLegacyRichText = (html: string | null | undefined): string => {
  if (!html) return ''
  if (html === '<p><br></p>') return ''
  if (!hasQuillMarkup(html)) return html

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const body = doc.body

  regroupQuillLists(body)
  convertQuillClasses(body)

  return body.innerHTML
}
